import { createColumnHelper } from "@tanstack/react-table";

import { FiSend, FiCheck, FiX } from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";

import CrudActions from "components/crud/CrudActions";

const columnHelper = createColumnHelper();

/* ==========================================================================
   DATE
   ========================================================================== */

const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "-";
    }

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

/* ==========================================================================
   CURRENCY
   ========================================================================== */

const formatCurrency = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "₹0.00";
    }

    const amount = Number(value);

    if (Number.isNaN(amount)) {
        return `₹${value}`;
    }

    return `₹${amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

/* ==========================================================================
   TEXT
   ========================================================================== */

const formatText = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    return String(value)
        .replace(/_/g, " ")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

/* ==========================================================================
   STATUS CLASS
   ========================================================================== */

const getStatusClass = (status) => {
    switch (status) {
        case "draft":
            return "bg-gray-50 text-gray-700 border-gray-200";

        case "submitted":
            return "bg-blue-50 text-blue-700 border-blue-200";

        case "approved":
            return "bg-green-50 text-green-700 border-green-200";

        case "rejected":
            return "bg-red-50 text-red-700 border-red-200";

        case "paid":
            return "bg-emerald-50 text-emerald-700 border-emerald-200";

        case "cancelled":
            return "bg-orange-50 text-orange-700 border-orange-200";

        default:
            return "bg-gray-50 text-gray-700 border-gray-200";
    }
};

/* ==========================================================================
   GROUP HELPERS
   ========================================================================== */

/**
 * Get Employee ID
 */
const getEmployeeId = (expense) => {
    return (
        expense?.employee?.id ??
        expense?.employeeId ??
        expense?.employee?.employeeId ??
        ""
    );
};

/**
 * Get expense date in YYYY-MM-DD format
 */
const getExpenseDateKey = (expense) => {
    if (!expense?.expenseDate) {
        return "";
    }

    const raw = String(expense.expenseDate);

    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
        return raw.slice(0, 10);
    }

    const date = new Date(expense.expenseDate);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return date.toISOString().slice(0, 10);
};

/**
 * Get Visit ID
 */
const getVisitId = (expense) => {
    return (
        expense?.visit?.id ??
        expense?.visitId ??
        null
    );
};

/**
 * Get group key.
 *
 * Same:
 * Employee + Date + Visit
 *
 * = same group
 */
const getGroupKey = (expense) => {
    const employeeId = String(
        getEmployeeId(expense)
    );

    const expenseDate = getExpenseDateKey(
        expense
    );

    const visitId = getVisitId(expense);

    /*
     * If visit exists:
     * Employee + Date + Visit
     */
    if (
        visitId !== null &&
        visitId !== undefined &&
        String(visitId).trim() !== ""
    ) {
        return [
            "visit",
            employeeId,
            expenseDate,
            String(visitId),
        ].join("_");
    }

    /*
     * No visit:
     * Keep individual expense separate.
     */
    return [
        "expense",
        employeeId,
        expenseDate,
        String(expense?.id ?? ""),
    ].join("_");
};

/**
 * IMPORTANT:
 *
 * Current Expenses.jsx grouped structure:
 *
 * {
 *     id: first.id,
 *     expenses: [
 *         expense1,
 *         expense2,
 *         expense3
 *     ]
 * }
 *
 * Older structure:
 *
 * {
 *     _groupItems: [...]
 * }
 *
 * Support both.
 */
const getGroupItems = (expense) => {
    /*
     * CURRENT GROUP FORMAT
     */
    if (
        Array.isArray(expense?.expenses) &&
        expense.expenses.length > 0
    ) {
        return expense.expenses;
    }

    /*
     * OLD GROUP FORMAT
     */
    if (
        Array.isArray(expense?._groupItems) &&
        expense._groupItems.length > 0
    ) {
        return expense._groupItems;
    }

    /*
     * Single expense
     */
    if (expense) {
        return [expense];
    }

    return [];
};

/**
 * Get group count
 */
const getExpenseGroupCount = (expense) => {
    return getGroupItems(expense).length;
};

/**
 * Get total claimed amount
 */
const getGroupClaimedAmount = (expense) => {
    const items = getGroupItems(expense);

    return items.reduce((total, item) => {
        const amount = Number(
            item?.claimedAmount ?? 0
        );

        return Number.isFinite(amount)
            ? total + amount
            : total;
    }, 0);
};

/**
 * Get total approved amount
 */
const getGroupApprovedAmount = (expense) => {
    const items = getGroupItems(expense);

    return items.reduce((total, item) => {
        const amount = Number(
            item?.approvedAmount ?? 0
        );

        return Number.isFinite(amount)
            ? total + amount
            : total;
    }, 0);
};

/**
 * Get combined expense types
 */
const getGroupExpenseTypes = (expense) => {
    const items = getGroupItems(expense);

    const types = [
        ...new Set(
            items
                .map(
                    (item) =>
                        item?.expenseType
                )
                .filter(Boolean)
        ),
    ];

    if (!types.length) {
        return "-";
    }

    return types
        .map((type) => formatText(type))
        .join(" + ");
};

/**
 * Get combined titles
 */
const getGroupTitle = (expense) => {
    const items = getGroupItems(expense);

    const titles = [
        ...new Set(
            items
                .map(
                    (item) =>
                        item?.title
                )
                .filter(Boolean)
        ),
    ];

    if (!titles.length) {
        return "-";
    }

    return titles.join(" + ");
};

/**
 * Get group status
 */
const getGroupStatus = (expense) => {
    const items = getGroupItems(expense);

    const statuses = items
        .map((item) => item?.status)
        .filter(Boolean);

    if (!statuses.length) {
        return expense?.status || "draft";
    }

    /*
     * All paid
     */
    if (
        statuses.every(
            (status) =>
                status === "paid"
        )
    ) {
        return "paid";
    }

    /*
     * All approved / paid
     */
    if (
        statuses.every(
            (status) =>
                status === "approved" ||
                status === "paid"
        )
    ) {
        return "approved";
    }

    /*
     * Any submitted
     */
    if (
        statuses.some(
            (status) =>
                status === "submitted"
        )
    ) {
        return "submitted";
    }

    /*
     * Any rejected
     */
    if (
        statuses.some(
            (status) =>
                status === "rejected"
        )
    ) {
        return "rejected";
    }

    /*
     * All cancelled
     */
    if (
        statuses.every(
            (status) =>
                status === "cancelled"
        )
    ) {
        return "cancelled";
    }

    /*
     * All draft
     */
    if (
        statuses.every(
            (status) =>
                status === "draft"
        )
    ) {
        return "draft";
    }

    return "draft";
};

/* ==========================================================================
   SUBMIT HELPERS
   ========================================================================== */

/**
 * At least one item can be submitted
 */
const canSubmitGroup = (expense) => {
    const items = getGroupItems(expense);

    if (!items.length) {
        return false;
    }

    return items.some(
        (item) =>
            item?.status === "draft" ||
            item?.status === "rejected"
    );
};

/**
 * Group contains pending submit items
 */
const hasPendingSubmitItems = (expense) => {
    const items = getGroupItems(expense);

    return items.some(
        (item) =>
            item?.status === "draft" ||
            item?.status === "rejected"
    );
};

/**
 * Group contains submitted items
 */
const hasSubmittedItems = (expense) => {
    const items = getGroupItems(expense);

    return items.some(
        (item) =>
            item?.status === "submitted"
    );
};

/**
 * Group contains approved items
 */
const hasApprovedItems = (expense) => {
    const items = getGroupItems(expense);

    return items.some(
        (item) =>
            item?.status === "approved"
    );
};

/* ==========================================================================
   MAIN COLUMNS
   ========================================================================== */

export const expenseColumns = ({
    onView,
    onEdit,
    onDelete,
    onSubmit,
    onApprove,
    onReject,
    onMarkPaid,
} = {}) => {

    /* ======================================================================
       CURRENT USER
       ====================================================================== */

    let user = {};

    try {
        user = JSON.parse(
            localStorage.getItem("user") || "{}"
        );
    } catch (error) {
        console.error(
            "Unable to parse logged-in user",
            error
        );
    }

    const permissions = Array.isArray(
        user?.permissions
    )
        ? user.permissions
        : [];

    const roles = Array.isArray(
        user?.roles
    )
        ? user.roles
        : [];

    const hasPermission = (
        permission
    ) => {
        return permissions.includes(
            permission
        );
    };

    /* ======================================================================
       ROLES
       ====================================================================== */

    const isAdmin =
        roles.includes("admin") ||
        roles.includes("super_admin");

    const isManager =
        roles.includes("manager") ||
        roles.includes("team_leader") ||
        roles.includes("regional_manager");

    const isSalesPerson =
        roles.includes("sales_person");

    /* ======================================================================
       SALES ACTIONS
       ====================================================================== */

    const canSalesActions =
        isSalesPerson &&
        (
            hasPermission(
                "expenses.update"
            ) ||
            hasPermission(
                "expenses.manage"
            )
        );

    const canDeleteOwnExpense =
        isSalesPerson &&
        (
            hasPermission(
                "expenses.delete"
            ) ||
            hasPermission(
                "expenses.manage"
            )
        );

    /* ======================================================================
       REVIEW ACTIONS
       ====================================================================== */

    const canReview =
        (
            isAdmin ||
            isManager
        ) &&
        (
            hasPermission(
                "expenses.approve"
            ) ||
            hasPermission(
                "expenses.manage"
            )
        );

    /* ======================================================================
       MARK PAID
       ====================================================================== */

    const canMarkPaid =
        isAdmin &&
        (
            hasPermission(
                "expenses.approve"
            ) ||
            hasPermission(
                "expenses.manage"
            )
        );

    /* ======================================================================
       RETURN COLUMNS
       ====================================================================== */

    return [

        /* ==================================================================
           DATE
           ================================================================== */

        columnHelper.accessor(
            "expenseDate",
            {
                id: "expenseDate",

                header: "Date",

                cell: (info) => (
                    formatDate(
                        info.getValue()
                    )
                ),
            }
        ),

        /* ==================================================================
           EMPLOYEE
           ================================================================== */

        columnHelper.accessor(
            "employee.name",
            {
                id: "employee",

                header: "Employee",

                cell: (info) => {
                    const expense =
                        info.row.original;

                    const employee =
                        expense?.employee;

                    return (
                        <div>
                            <div
                                className="
                                    font-semibold
                                    text-navy-700
                                    dark:text-white
                                "
                            >
                                {
                                    employee?.name ||
                                    employee?.fullName ||
                                    "-"
                                }
                            </div>

                            {employee?.employeeCode && (
                                <div
                                    className="
                                        text-xs
                                        text-gray-500
                                    "
                                >
                                    {
                                        employee.employeeCode
                                    }
                                </div>
                            )}
                        </div>
                    );
                },
            }
        ),

        /* ==================================================================
           VISIT
           ================================================================== */

        columnHelper.display({
            id: "visit",

            header: "Visit",

            cell: ({ row }) => {
                const expense =
                    row.original;

                const visit =
                    expense?.visit;

                return (
                    <div
                        className="
                            max-w-[220px]
                            truncate
                            text-sm
                            text-gray-700
                            dark:text-white
                        "
                        title={
                            visit?.school?.name ||
                            visit?.party?.name ||
                            visit?.purpose ||
                            ""
                        }
                    >
                        {
                            visit?.school?.name ||
                            visit?.party?.name ||
                            visit?.purpose ||
                            (
                                visit?.id
                                    ? `Visit #${visit.id}`
                                    : "-"
                            )
                        }
                    </div>
                );
            },
        }),

       
        /* ==================================================================
           EXPENSE TYPES
           ================================================================== */

        columnHelper.display({
            id: "expenseTypes",

            header: "Expense",

            cell: ({ row }) => {
                const expense =
                    row.original;

                const count =
                    getExpenseGroupCount(
                        expense
                    );

                return (
                    <div>
                        <div
                            className="
                                font-medium
                                capitalize
                                text-gray-800
                                dark:text-white
                            "
                        >
                            {
                                getGroupExpenseTypes(
                                    expense
                                )
                            }
                        </div>

                        {count > 1 && (
                            <div
                                className="
                                    mt-1
                                    text-xs
                                    text-gray-500
                                "
                            >
                                {count} expenses
                            </div>
                        )}
                    </div>
                );
            },
        }),

        /* ==================================================================
           TITLE
           ================================================================== */

        columnHelper.display({
            id: "title",

            header: "Title",

            cell: ({ row }) => {
                const expense =
                    row.original;

                const title =
                    getGroupTitle(
                        expense
                    );

                return (
                    <span
                        className="
                            block
                            max-w-[220px]
                            truncate
                            text-navy-700
                            dark:text-white
                        "
                        title={title}
                    >
                        {title}
                    </span>
                );
            },
        }),

        /* ==================================================================
           CLAIMED
           ================================================================== */

        columnHelper.display({
            id: "claimedAmount",

            header: "Claimed",

            cell: ({ row }) => (
                <span
                    className="
                        whitespace-nowrap
                        font-semibold
                        text-gray-800
                        dark:text-white
                    "
                >
                    {
                        formatCurrency(
                            getGroupClaimedAmount(
                                row.original
                            )
                        )
                    }
                </span>
            ),
        }),

        /* ==================================================================
           APPROVED
           ================================================================== */

        columnHelper.display({
            id: "approvedAmount",

            header: "Approved",

            cell: ({ row }) => (
                <span
                    className="
                        whitespace-nowrap
                        font-semibold
                        text-gray-800
                        dark:text-white
                    "
                >
                    {
                        formatCurrency(
                            getGroupApprovedAmount(
                                row.original
                            )
                        )
                    }
                </span>
            ),
        }),

        /* ==================================================================
           STATUS
           ================================================================== */

        columnHelper.display({
            id: "status",

            header: "Status",

            cell: ({ row }) => {
                const status =
                    getGroupStatus(
                        row.original
                    );

                return (
                    <span
                        className={`
                            inline-flex
                            items-center
                            justify-center
                            whitespace-nowrap
                            rounded-full
                            border
                            px-3
                            py-1
                            text-xs
                            font-semibold
                            capitalize
                            ${getStatusClass(status)}
                        `}
                    >
                        {formatText(status)}
                    </span>
                );
            },
        }),

        /* ==================================================================
           ACTIONS
           ================================================================== */

        columnHelper.display({
            id: "actions",

            header: "Actions",

            cell: ({ row }) => {
                const expense =
                    row.original;

                /*
                 * IMPORTANT:
                 *
                 * This now reads:
                 *
                 * expense.expenses
                 *
                 * first.
                 *
                 * So grouped rows contain ALL
                 * database expense records.
                 */
                const groupItems =
                    getGroupItems(
                        expense
                    );

                const groupStatus =
                    getGroupStatus(
                        expense
                    );

                const isSingleExpense =
                    groupItems.length === 1;

                const singleExpense =
                    groupItems[0];

                /* ==========================================================
                   APPROVE
                   ========================================================== */

                const canApproveGroup =
                    canReview &&
                    hasSubmittedItems(
                        expense
                    );

                /* ==========================================================
                   REJECT
                   ========================================================== */

                const canRejectGroup =
                    canReview &&
                    hasSubmittedItems(
                        expense
                    );

                /* ==========================================================
                   MARK PAID
                   ========================================================== */

                const canPayGroup =
                    canMarkPaid &&
                    hasApprovedItems(
                        expense
                    );

                /* ==========================================================
                   EDIT
                   ========================================================== */

                const canEdit =
                    isSingleExpense &&
                    canSalesActions &&
                    [
                        "draft",
                        "rejected",
                    ].includes(
                        singleExpense?.status
                    );

                /* ==========================================================
                   SUBMIT GROUP
                   ========================================================== */

                const canSubmit =
                    canSalesActions &&
                    hasPendingSubmitItems(
                        expense
                    );

                /* ==========================================================
                   DELETE / CANCEL
                   ========================================================== */

                const canCancel =
                    isSingleExpense &&
                    canDeleteOwnExpense &&
                    [
                        "draft",
                        "rejected",
                    ].includes(
                        singleExpense?.status
                    );

                return (
                    <div
                        className="
                            flex
                            items-center
                            gap-2
                            whitespace-nowrap
                        "
                    >

                        {/* ==================================================
                            CRUD ACTIONS
                           ================================================== */}

                        <CrudActions
                            showView={
                                typeof onView ===
                                "function"
                            }

                            onView={
                                typeof onView ===
                                "function"
                                    ? () =>
                                          onView(
                                              expense
                                          )
                                    : undefined
                            }

                            viewTitle={
                                groupItems.length > 1
                                    ? "View Expense Group"
                                    : "View Expense"
                            }

                            showEdit={
                                canEdit
                            }

                            onEdit={
                                canEdit
                                    ? () =>
                                          onEdit(
                                              singleExpense
                                          )
                                    : undefined
                            }

                            editTitle="Edit Expense"

                            showDelete={
                                canCancel
                            }

                            onDelete={
                                canCancel
                                    ? () =>
                                          onDelete(
                                              singleExpense.id
                                          )
                                    : undefined
                            }

                            deleteTitle="Cancel Expense"

                            showApprove={
                                canApproveGroup
                            }

                            onApprove={
                                canApproveGroup
                                    ? () =>
                                          onApprove(
                                              expense
                                          )
                                    : undefined
                            }

                            approveTitle={
                                groupItems.length > 1
                                    ? `Approve ${groupItems.length} Expenses`
                                    : "Approve Expense"
                            }

                            showReject={
                                canRejectGroup
                            }

                            onReject={
                                canRejectGroup
                                    ? () =>
                                          onReject(
                                              expense
                                          )
                                    : undefined
                            }

                            rejectTitle={
                                groupItems.length > 1
                                    ? `Reject ${groupItems.length} Expenses`
                                    : "Reject Expense"
                            }
                        />

                        {/* ==================================================
                            SUBMIT GROUP
                           ================================================== */}

                        {canSubmit &&
                            typeof onSubmit ===
                                "function" && (
                                <button
                                    type="button"

                                    onClick={() => {
                                        /*
                                         * Get ALL IDs
                                         * from current group.
                                         */
                                        const ids =
                                            groupItems
                                                .map(
                                                    (
                                                        item
                                                    ) =>
                                                        item?.id
                                                )
                                                .filter(
                                                    (
                                                        id
                                                    ) =>
                                                        id !==
                                                            undefined &&
                                                        id !==
                                                            null &&
                                                        String(
                                                            id
                                                        ).trim() !==
                                                            ""
                                                )
                                                .map(
                                                    (
                                                        id
                                                    ) =>
                                                        String(
                                                            id
                                                        )
                                                );

                                        console.log(
                                            "===================================="
                                        );

                                        console.log(
                                            "GROUP SUBMIT"
                                        );

                                        console.log(
                                            "Group expense count:",
                                            groupItems.length
                                        );

                                        console.log(
                                            "Group items:",
                                            groupItems
                                        );

                                        console.log(
                                            "Submitting expense IDs:",
                                            ids
                                        );

                                        console.log(
                                            "===================================="
                                        );

                                        if (
                                            !ids.length
                                        ) {
                                            console.error(
                                                "No expense IDs found."
                                            );

                                            return;
                                        }

                                        /*
                                         * Parent handler
                                         * will submit each ID
                                         * separately.
                                         */
                                        onSubmit(
                                            ids
                                        );
                                    }}

                                    title={
                                        groupItems.length >
                                        1
                                            ? `Submit ${groupItems.length} Expenses`
                                            : "Submit Expense"
                                    }

                                    aria-label={
                                        groupItems.length >
                                        1
                                            ? `Submit ${groupItems.length} Expenses`
                                            : "Submit Expense"
                                    }

                                    className="
                                        inline-flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-indigo-200
                                        bg-indigo-50
                                        text-indigo-600
                                        transition-all
                                        duration-150
                                        hover:border-indigo-300
                                        hover:bg-indigo-100
                                        hover:text-indigo-700
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-indigo-200
                                    "
                                >
                                    <FiSend
                                        size={16}
                                        strokeWidth={2}
                                    />
                                </button>
                            )}

                        {/* ==================================================
                            APPROVE GROUP
                           ================================================== */}

                        {canApproveGroup &&
                            typeof onApprove ===
                                "function" && (
                                <button
                                    type="button"

                                    onClick={() =>
                                        onApprove(
                                            expense
                                        )
                                    }

                                    title={
                                        groupItems.length >
                                        1
                                            ? `Approve ${groupItems.length} Expenses`
                                            : "Approve Expense"
                                    }

                                    aria-label={
                                        groupItems.length >
                                        1
                                            ? `Approve ${groupItems.length} Expenses`
                                            : "Approve Expense"
                                    }

                                    className="
                                        inline-flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-green-200
                                        bg-green-50
                                        text-green-600
                                        transition-all
                                        duration-150
                                        hover:border-green-300
                                        hover:bg-green-100
                                        hover:text-green-700
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-green-200
                                    "
                                >
                                    <FiCheck
                                        size={16}
                                        strokeWidth={2}
                                    />
                                </button>
                            )}

                        {/* ==================================================
                            REJECT GROUP
                           ================================================== */}

                        {canRejectGroup &&
                            typeof onReject ===
                                "function" && (
                                <button
                                    type="button"

                                    onClick={() =>
                                        onReject(
                                            expense
                                        )
                                    }

                                    title={
                                        groupItems.length >
                                        1
                                            ? `Reject ${groupItems.length} Expenses`
                                            : "Reject Expense"
                                    }

                                    aria-label={
                                        groupItems.length >
                                        1
                                            ? `Reject ${groupItems.length} Expenses`
                                            : "Reject Expense"
                                    }

                                    className="
                                        inline-flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-red-200
                                        bg-red-50
                                        text-red-600
                                        transition-all
                                        duration-150
                                        hover:border-red-300
                                        hover:bg-red-100
                                        hover:text-red-700
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-red-200
                                    "
                                >
                                    <FiX
                                        size={16}
                                        strokeWidth={2}
                                    />
                                </button>
                            )}

                        {/* ==================================================
                            MARK PAID GROUP
                           ================================================== */}

                        {canPayGroup &&
                            typeof onMarkPaid ===
                                "function" && (
                                <button
                                    type="button"

                                    onClick={() =>
                                        onMarkPaid(
                                            expense
                                        )
                                    }

                                    title={
                                        groupItems.length >
                                        1
                                            ? `Mark ${groupItems.length} Expenses Paid`
                                            : "Mark Expense Paid"
                                    }

                                    aria-label={
                                        groupItems.length >
                                        1
                                            ? `Mark ${groupItems.length} Expenses Paid`
                                            : "Mark Expense Paid"
                                    }

                                    className="
                                        inline-flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-emerald-200
                                        bg-emerald-50
                                        text-emerald-600
                                        transition-all
                                        duration-150
                                        hover:border-emerald-300
                                        hover:bg-emerald-100
                                        hover:text-emerald-700
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-emerald-200
                                    "
                                >
                                    <FaRupeeSign
                                        size={15}
                                    />
                                </button>
                            )}

                    </div>
                );
            },
        }),
    ];
};

export default expenseColumns;